// Fetches the client's public IPv4 from ipify.org for audit-log clustering.
//
// Why this exists: mobile carriers rotate the client's IPv6 per request, so the
// server-observed IP cannot correlate multiple signups from the same actor.
// ipify.org only resolves IPv4 DNS, forcing a parallel IPv4 lookup that returns
// a stable carrier-level IPv4 regardless of the main connection's protocol.
// Sent to Cloud Functions as an audit-log field; treated as a correlation hint,
// not as authoritative origin (spoofable).
//
// Single-session cache: first call starts the fetch, stores the Promise, and
// every subsequent call resolves with the same value. Call primeClientReportedIpv4()
// at app boot so by audit-log-write time the Promise is already resolved and
// actions incur no latency. If the fetch fails (blocked, offline, timeout,
// non-200, malformed JSON), the cached value is '' — we accept the miss rather
// than retry, since a failure usually indicates a blocker that won't clear
// within the session.

const IPIFY_URL = 'https://api.ipify.org?format=json';
const TIMEOUT_MS = 3000;

let cached: Promise<string> | null = null;

const fetchOnce = async (): Promise<string> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const response = await fetch(IPIFY_URL, { signal: controller.signal });
    if (!response.ok) return '';
    const body = await response.json();
    return typeof body?.ip === 'string' ? body.ip : '';
  } catch {
    return '';
  } finally {
    clearTimeout(timeoutId);
  }
};

export const fetchClientReportedIpv4 = (): Promise<string> => {
  if (!cached) cached = fetchOnce();
  return cached;
};

// Fire-and-forget warm-up. Call at app boot so the first audit-log action
// resolves instantly instead of waiting on the ipify round-trip. Safe to call
// multiple times — subsequent calls are no-ops once the cache is populated.
export const primeClientReportedIpv4 = (): void => {
  void fetchClientReportedIpv4();
};
